import { Box, Grid, Stack, Typography } from '@mui/material';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAnswerAction5Api } from 'src/api/useAnswerAction5Api';
import { LoadableComponent } from 'src/components/app-components/bonsai/LoadableComponent';
import { StyledAgGrid } from 'src/components/app-components/StyledAgGrid';
import useLocales from 'src/hooks/useLocales';
import useSettings from 'src/hooks/useSettings';
import useStatus from 'src/hooks/useStatus';
import { useAppSelector } from 'src/redux/store';
import { formatterDateTime } from 'src/utils/formatDateTime';
import { showUserName } from 'src/utils/gridValueUtils';

interface AnswerStatus {
  status: string;
  statusText: string;
  updatedAt: Date | null;
  updatedUserName?: string | null;
  updatedUserNameEng?: string | null;
}

//日時フォーマット定義
const DEFAULT_DATE_TIME_VALUE = {
  DEFAULT_OPENED_DATE_TIME: '1970-01-01T00:00:00.000Z',
  DEFAULT_CLOSED_DATE_TIME: '9999-12-31T00:00:00.000Z',
} as const;

export const Action5InfoDataGrid: React.FC = () => {
  const { t } = useTranslation();
  const { toStringAnswerStatus } = useStatus();

  const historyApi = useAnswerAction5Api();

  const enqueteAnswerAction5Data = useAppSelector((state) => state.enqueteAnswer.enqueteAnswerAction5Data);

  // state
  const [rowData, setRowData] = useState<AnswerStatus[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // lang
  const { isJapanese } = useLocales();

  // data table theme
  const { themeMode } = useSettings();
  const isLight = themeMode === 'light';

  //-------------------------------------------------------------
  // Default column define
  //-------------------------------------------------------------
  const defaultColDef: ColDef = {
    flex: 1,
    sortable: true,
    resizable: true,
  };

  //-------------------------------------------------------------
  // Column define
  //-------------------------------------------------------------
  const columnDefs: (ColDef | ColGroupDef)[] = [
    {
      headerName: t('answerConfirmStatus.actionHistoryTable.gridColDef.answerStatus'),
      field: 'statusText',
      tooltipValueGetter: (param) => {
        return param.data.statusText;
      },
    },
    {
      headerName: t('answerConfirmStatus.actionHistoryTable.gridColDef.updateBy'),
      field: isJapanese ? 'updatedUserName' : 'updatedUserNameEng',
      valueGetter: (params) => showUserName(params, 'updatedUserName', 'updatedUserNameEng', isJapanese),
      tooltipValueGetter: (params) => showUserName(params, 'updatedUserName', 'updatedUserNameEng', isJapanese),
    },
    {
      headerName: t('answerConfirmStatus.actionHistoryTable.gridColDef.updateDate'),
      field: 'updatedAt',
      resizable: false,
      valueFormatter: (param) => {
        const date = param.data.updatedAt || DEFAULT_DATE_TIME_VALUE.DEFAULT_OPENED_DATE_TIME;
        return formatterDateTime(date);
      },
      tooltipValueGetter: (param) => {
        const date = param.data.updatedAt || DEFAULT_DATE_TIME_VALUE.DEFAULT_OPENED_DATE_TIME;
        return formatterDateTime(date);
      },
    },
  ];

  //-------------------------------------------------------------
  // Refresh row data
  //-------------------------------------------------------------
  const refreshRowData = async () => {
    if (enqueteAnswerAction5Data?.respondentTargetId) {
      setIsLoading(true);

      // Get answer hisotry
      const history = await historyApi.getAnswerHistoryAction5(enqueteAnswerAction5Data.respondentTargetId);
      if (!history) {
        setRowData([]);
        setIsLoading(false);
        return;
      }

      // 更新日時順（降順）
      const sortedList = _.orderBy(
        history,
        (obj) => {
          return obj.updatedAt !== null ? new Date(obj.updatedAt) : null;
        },
        'desc',
      );

      const answerStatusDatas: AnswerStatus[] = [];
      sortedList.forEach((sortedListData) => {

        const answerStatusData: AnswerStatus = {
          ...sortedListData,
          statusText: toStringAnswerStatus(sortedListData.status),
          updatedUserName: sortedListData?.userName ?? '',
          updatedUserNameEng: sortedListData?.userNameEng ?? '',
        };
        answerStatusDatas.push(answerStatusData);
      });

      setRowData(answerStatusDatas);
      setIsLoading(false);
    }
  };

  //-------------------------------------------------------------
  // useEffect
  //-------------------------------------------------------------
  useEffect(() => {
    refreshRowData();
  }, [isJapanese]); //eslint-disable-line

  useEffect(() => {
    refreshRowData();
  }, [enqueteAnswerAction5Data]); //eslint-disable-line

  return (
    <Stack style={{ marginTop: 0 }}>
      {/* header block*/}
      <Grid container>
        <Grid item xs={12}>
          <Stack direction={'row'}>
            <Typography>{t('answerConfirmStatus.actionHistoryTable.gridTitle')}</Typography>
          </Stack>
        </Grid>
      </Grid>

      {/* data table */}
      <Grid container>
        <Grid item xs={12} sm={6}>
          <Box className={isLight ? 'ag-theme-alpine' : 'ag-theme-alpine-dark'} 
            sx={{ height: 171, width: '100%' }}>
            <LoadableComponent isLoading={isLoading}>
              <StyledAgGrid defaultColDef={defaultColDef} coldef={columnDefs} rows={rowData} suppressAutoSize={true} tooltipShowDelay={0} />
            </LoadableComponent>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};
